import { Eye, EyeOff, LucideArrowUpRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "./ui/button";
import { FC, useState } from "react";
import { useFormatAmountWithCurrency } from "@/hooks/use-format-with-currency";
import { useCardBalance } from "@/hooks/use-queries";
import { resolveCardStyle } from "@/pages/dashboard/shop/page";

type CardProps = {
  className?: string;
  card: Card;
  showCta?: boolean;
};

const Card: FC<CardProps> = ({ className, card, showCta = true }) => {
  const navigate = useNavigate();
  const formatAmount = useFormatAmountWithCurrency();
  const { balance } = useCardBalance(card);
  const [showAmount, setShowAmount] = useState(true);
  const isFrozen = card?.status === "Frozen";
  const style = resolveCardStyle(card?.bin, card?.network);

  return (
    <div
      className={`group font-dm-sans bg-dark-card-4 relative aspect-3/2 w-70 shrink-0  rounded-xl ${isFrozen ? "grayscale" : ""} ${className}`}
      style={{ background: style.background }}
      onTouchStart={(e) => {
        e.currentTarget.focus();
      }}
      tabIndex={1}
    >
      {showCta && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          onTouchStart={() => {}}
          className="absolute inset-0 z-30 hidden items-center justify-center bg-black/70 group-hover:flex group-focus:flex group-active:flex"
        >
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            exit={{ opacity: 0, scale: 0.5 }}
            onClick={() => navigate(`/dashboard/cards/${card.id}`)}
            className="bg-dark-primary-gradient text-dark-text-400 border-dark-stroke-5 flex cursor-pointer items-center gap-2.5 rounded-full border px-4 py-1 transition-all duration-300 hover:opacity-80"
          >
            <div className="bg-dark-text-400/20 flex size-4 items-center justify-center rounded-full">
              <LucideArrowUpRight className="size-3" />{" "}
            </div>
            <span>View</span>
          </motion.button>
        </motion.div>
      )}
      {/* The card face is designer artwork; line art, glow and texture are
          baked into it, so there are no decorative overlay layers here.
          Frozen cards desaturate that artwork rather than swapping in a
          separate grey fill. */}
      <div
        className={`relative z-10 flex h-full flex-col justify-between p-4 ${isFrozen ? "pointer-events-none opacity-50" : ""}`}
      >
        <div></div>
        < div className="absolute top-7 right-3 flex w-[32%] justify-end">
        <img
          src={style.logoImage ?? "/images/logo-transparent-light.svg"}
          className="h-auto w-22 self-end object-contain"
          alt="card logo"
        /></div>
        <div className="text-white">
          <div className="flex items-center gap-2 font-semibold">
            <span>
              {showAmount ? `${formatAmount(balance?.available)}` : "****"}
            </span>
            <Button
              variant={"ghost"}
              onClick={(e) => {
                e.stopPropagation();
                setShowAmount(!showAmount);
              }}
              className="size-auto cursor-pointer !p-1 hover:bg-white/10"
            >
              {showAmount ? (
                <EyeOff className="size-4 text-white/40" />
              ) : (
                <Eye className="size-4 text-white/40" />
              )}
            </Button>
          </div>
          <div className="flex items-center justify-between pt-2">
            <div>
              <span className="text-xs font-medium text-white">
                **** **** **** {card?.last4}
              </span>{" "}
              <div className="text-[10px] font-medium text-white capitalize">
                {card?.firstName} {card?.lastName}
              </div>
            </div>
            {card?.network === "VISA" || card?.network === "Visa" ? (
              <img src="/images/visa-white.png" className="w-8" alt="" />
            ) : (
              <img src="/images/mastercard-logo.svg" className="w-8" alt="" />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Card;
